import React, { useEffect, useRef, useState } from "react";
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon, ExclamationTriangleIcon, LockClosedIcon, LockOpenIcon, SignalSlashIcon, WifiIcon, XMarkIcon } from "@heroicons/react/20/solid";

import { CHART_TYPE_OPTIONS, ChartData, ChartType } from "cindra/charts/types";
import { CindraChartRenderer } from "cindra/charts";
import { CindraChatMessageContent } from "cindra/CindraChatMessageContent";
import { CindraPatientInfoPanel } from "cindra/CindraPatientInfoPanel";
import { getAgentForLLM, getSelectedLLM, type LLMId, setSelectedLLM } from "cindra/MastraClient";
import { getAuthToken } from "services/AccountServices";
import { parsePatientInfoMarkdown } from "cindra/CindraPatientInfoUtils";
import { extractStatusMessage } from "cindra/CindraTextUtils";
import { useMCP } from "mcp/hooks/useMCP";
import { useMCPAuth } from "mcp/hooks/useMCPAuth";

// Keyboard commands configuration
interface KeyboardCommand {
    key: string;
    description: string;
}

const KEYBOARD_COMMANDS: KeyboardCommand[] = [
    { key: "Enter", description: "Send message" },
    { key: "Shift+Enter", description: "New line" },
    { key: "Esc", description: "Clear input and cancel request" },
    { key: "↑ (Up Arrow)", description: "Navigate to previous message in history" },
    { key: "↓ (Down Arrow)", description: "Navigate to next message in history" },
    { key: "/help", description: "Show this help message" }
];

interface Message {
    id: string;
    role: "user" | "assistant" | "system";
    content: string | null;
    timestamp: Date;
    isError?: boolean;
    errorType?: "api_key" | "network" | "server" | "auth" | "unknown";
    statusMessage?: string;
    isConnectivityStatus?: boolean;
    isHelpMessage?: boolean;                // Flag for help command messages
    patientInfoMarkdown?: string;           // Markdown from get_patient_information tool
    patientName?: string;                   // Patient name from get_patient_information tool
    chartData?: ChartData;                  // Chart data from create_chart tool
    phiAnonymized?: boolean;                // PHI was anonymized by MCP server
    phiDeanonymized?: boolean;              // PHI was deanonymized by agent
    phiDeanonymizationEnabled?: boolean;    // Agent had permission to deanonymize
    phiEncryptedMapAvailable?: boolean;     // Encrypted map was available
    phiViolation?: boolean;                 // Security violation detected
}

interface LLMProvider {
    id: string;
    name: string;
    model: string;
    provider: "anthropic" | "google" | "openai";
}

const LLM_PROVIDERS: LLMProvider[] = [
    {
        id: "claude-haiku-4-5",
        name: "Claude Haiku 4.5",
        provider: "anthropic",
        model: "claude-haiku-4-5",
    },
    // {
    //     id: "claude-sonnet-4-5-20250929",
    //     name: "Claude Sonnet 4.5",
    //     provider: "anthropic",
    //     model: "claude-sonnet-4-5-20250929",
    // },
    // {
    //     id: "gemini-3-pro-preview",
    //     name: "Gemini 3 Pro Preview",
    //     provider: "google",
    //     model: "gemini-3-pro-preview",
    // },
    // {
    //     id: "gpt-5.1",
    //     name: "GPT 5.1",
    //     provider: "openai",
    //     model: "pt-5.1",
    // },
];

export function CindraChat() {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [historyIndex, setHistoryIndex] = useState<number>(-1); // -1 means not navigating history
    const [selectedChartType, setSelectedChartType] = useState<ChartType>('none');
    const [hoveredChartType, setHoveredChartType] = useState<ChartType | null>(null);
    const [currentPatient, setCurrentPatient] = useState<string | null>(null);
    const [hasInteracted, setHasInteracted] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Debug feature: PHI deanonymization override (only available when enabled)
    const isPhiPrivacyOverrideEnabled = import.meta.env.VITE_CINDRA_PHI_PRIVACY_OVERRIDE_ENABLED === 'true';
    const [phiPrivacyMode, setPhiPrivacyMode] = useState(false); // false = not private (deanonymize), true = private (keep anonymized)

    // Initialize selectedLLM from cookie on mount
    const [selectedLLM, setSelectedLLMState] = useState<LLMProvider>(() => {
        const savedLLMId = getSelectedLLM();
        return LLM_PROVIDERS.find(p => p.id === savedLLMId) || LLM_PROVIDERS[0];
    });
    const mcpState = useMCP();
    const mcpAuth = useMCPAuth();

    // Track previous connection state to detect changes
    const prevConnectionState = useRef<{
        isConnected: boolean;
        hasAuthToken: boolean;
        tokenValidated: boolean | null;
        reconnectAttempts: number;
    }>({
        isConnected: mcpState.isCindraConnected,
        hasAuthToken: mcpAuth.hasAuthToken,
        tokenValidated: mcpAuth.tokenValidated,
        reconnectAttempts: mcpState.reconnectAttempts
    });

    // Set up initial system message with authentication context
    useEffect(() => {
        if (mcpState.isCindraConnected && messages.length === 0) {
            const createInitialMessage = async () => {
                setMessages([{
                    id: "0",
                    role: "assistant",
                    content: `Hello! I'm Cindra, your Karuna AI assistant. How can I help you today?`,
                    timestamp: new Date()
                }]);
            };

            void createInitialMessage();
        }
    }, [mcpState.isCindraConnected, mcpState.tools.length, mcpState.resources.length, messages.length, mcpAuth.tokenValidated, mcpAuth.validatedUser, mcpAuth.hasAuthToken]);

    // Auto-scroll to the latest message when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, loading]);

    // Monitor MCP connectivity changes and add status messages to chat
    useEffect(() => {
        const prevState = prevConnectionState.current;
        const currentState = {
            isConnected: mcpState.isCindraConnected,
            hasAuthToken: mcpAuth.hasAuthToken,
            tokenValidated: mcpAuth.tokenValidated,
            reconnectAttempts: mcpState.reconnectAttempts
        };

        // Connection lost
        if (prevState.isConnected && !currentState.isConnected && messages.length > 0) {
            const disconnectMessage: Message = {
                id: `mcp-disconnect-${Date.now()}`,
                role: "system",
                content: "🔌 **Connection Lost** - Lost connection to Cindra MCP server. Attempting to reconnect...",
                timestamp: new Date(),
                isConnectivityStatus: true
            };
            setMessages(prev => [...prev, disconnectMessage]);
        }

        // Connection restored - remove any reconnecting messages and add restoration message
        if (!prevState.isConnected && currentState.isConnected && messages.length > 0) {
            setMessages(prev => {
                // Remove any existing reconnecting messages
                const filteredMessages = prev.filter(msg =>
                    !(msg.isConnectivityStatus && (
                        msg.content.includes("Connection Lost") ||
                        msg.content.includes("Reconnecting")
                    ))
                );

                // Add restoration message only if we had disconnection messages
                const hadDisconnectionMessages = prev.some(msg =>
                        msg.isConnectivityStatus && (
                            msg.content.includes("Connection Lost") ||
                            msg.content.includes("Reconnecting")
                        )
                );

                if (hadDisconnectionMessages) {
                    const reconnectMessage: Message = {
                        id: `mcp-reconnect-${Date.now()}`,
                        role: "system",
                        content: "✅ **Connection Restored** - Successfully reconnected to Cindra.",
                        timestamp: new Date(),
                        isConnectivityStatus: true
                    };
                    return [...filteredMessages, reconnectMessage];
                }

                return filteredMessages;
            });
        }

        // Update or add reconnection attempt message (consolidate into single message)
        if (!currentState.isConnected && currentState.reconnectAttempts > 0 && messages.length > 0) {
            setMessages(prev => {
                // Remove any existing reconnecting message
                const filteredMessages = prev.filter(msg =>
                    !(msg.isConnectivityStatus && msg.content.includes("Reconnecting"))
                );

                // Add updated reconnecting message
                const attemptMessage: Message = {
                    id: `mcp-reconnecting`, // Fixed ID so it can be found and replaced
                    role: "system",
                    content: `🔄 **Reconnecting** - Attempt ${currentState.reconnectAttempts}. Cindra is temporarily unavailable.`,
                    timestamp: new Date(),
                    isConnectivityStatus: true
                };

                return [...filteredMessages, attemptMessage];
            });
        }

        // Update previous state
        prevConnectionState.current = currentState;
    }, [mcpState.isCindraConnected, mcpState.reconnectAttempts, mcpAuth.hasAuthToken, mcpAuth.tokenValidated, messages.length]);

    // Helper function to categorize and format errors
    const categorizeError = (error: Error | unknown): { type: Message["errorType"], message: string } => {
        const errorMessage = error instanceof Error ? error.message : String(error);

        // Try to extract the "error" field from embedded JSON in the error message
        let extractedError = errorMessage;
        const jsonMatch = errorMessage.match(/\{[^{}]*"error"\s*:\s*"([^"]+)"[^{}]*}/);
        if (jsonMatch) {
            extractedError = jsonMatch[1];
        }

        const lowerMessage = extractedError.toLowerCase();

        // Check for authentication errors first
        if (lowerMessage.includes("no authentication token") ||
            lowerMessage.includes("token") && lowerMessage.includes("authentication")) {
            return {
                type: "auth",
                message: `🔑 **Authentication Error**: No valid authentication token available. Please ensure you are logged into the main application. ${extractedError}`
            };
        }

        // Check for specific AI SDK error types
        if (extractedError.includes("AI_LoadAPIKeyError") || extractedError.includes("LoadAPIKeyError")) {
            return {
                type: "api_key",
                message: `🔑 **API Key Missing**: The AI model API key is not configured. Please set your environment variables and restart the Mastra development server. ${extractedError}`
            };
        }

        // Check for API key issues (broader check)
        if (lowerMessage.includes("api key") || lowerMessage.includes("unauthorized") && !lowerMessage.includes("token")) {
            return {
                type: "api_key",
                message: `❌ **API Key Error**: The selected model's API key is not configured or is invalid. Please check your environment variables. ${extractedError}`
            };
        }

        // Check for credit balance issues
        if (lowerMessage.includes("credit balance") || lowerMessage.includes("plans & billing")) {
            return {
                type: "api_key",
                message: `💳 **API Credit Issue**: ${extractedError}`
            };
        }

        // Check for network issues
        if (lowerMessage.includes("network") || lowerMessage.includes("fetch") || lowerMessage.includes("connection") || lowerMessage.includes("timeout")) {
            return {
                type: "network",
                message: `🌐 **Network Error**: Unable to connect to the AI service. Please check your internet connection and try again. ${extractedError}`
            };
        }

        // Check for server issues
        if (lowerMessage.includes("server") || lowerMessage.includes("500") || lowerMessage.includes("503")) {
            return {
                type: "server",
                message: `🔧 **Server Error**: The AI service is currently unavailable. Please try again in a moment. ${extractedError}`
            };
        }

        // Generic error
        return {
            type: "unknown",
            message: `⚠️ **Error**: ${extractedError}`
        };
    };

    // Helper function to strip markdown content from the main response text
    const stripMarkdownFromContent = (content: string, markdown: string, requestedFormat?: string): string | null => {
        if (!content) return null;

        // If agent requested 'markdown' only, they likely just want to return the tool result
        // If agent requested 'json' or 'both', they want to do analysis
        const agentWantsToAnalyze = requestedFormat === 'json' || requestedFormat === 'both';

        // If agent explicitly requested json/both format, keep all content (agent is analyzing)
        if (agentWantsToAnalyze) {
            return content;
        }

        // Calculate similarity between content and markdown to determine if agent added analysis
        const contentNormalized = content.toLowerCase().replace(/\s+/g, ' ').trim();
        const markdownNormalized = markdown.toLowerCase().replace(/\s+/g, ' ').trim();

        // If markdown is embedded in content, calculate overlap percentage
        let overlapRatio = 0;
        if (markdownNormalized.length > 0) {
            // Count how much of the markdown appears in the content
            const markdownWords = markdownNormalized.split(' ');
            const matchingWords = markdownWords.filter(word => contentNormalized.includes(word)).length;
            overlapRatio = matchingWords / markdownWords.length;
        }

        // If content is mostly the same as markdown (>70% overlap), the agent just returned the tool result
        // In this case, strip the markdown and keep only the intro text
        if (overlapRatio > 0.7) {
            // Find where the markdown starts in the content
            const markdownStartMatch = content.match(/\n\s*#+/);

            if (markdownStartMatch && markdownStartMatch.index !== undefined) {
                // Keep everything before the markdown starts
                const beforeMarkdown = content.substring(0, markdownStartMatch.index).trim();

                // Check if the intro text is itself mostly markdown (thin wrapper)
                // If the intro is just one line or contains markdown formatting, return null
                const introLines = beforeMarkdown.split('\n').filter(line => line.trim());
                const hasMarkdownHeader = beforeMarkdown.match(/^\s*#+/);
                const isJustWrapper = introLines.length <= 1 || hasMarkdownHeader;

                if (isJustWrapper) {
                    return null; // Don't show thin wrapper text, just show the patient panel
                }

                return beforeMarkdown || null;
            }

            // Try to find the markdown block start another way
            if (content.includes(markdown.substring(0, 50))) {
                const markdownIndex = content.indexOf(markdown.substring(0, 50));
                const beforeMarkdown = content.substring(0, markdownIndex).trim();

                // Check if intro is a thin wrapper
                const introLines = beforeMarkdown.split('\n').filter(line => line.trim());
                if (introLines.length <= 1) {
                    return null;
                }

                return beforeMarkdown || null;
            }
        }

        // If overlap is low (<70%), the agent added its own analysis/reformulation
        // Keep the entire content as the agent intended
        return content;
    };

    const sendMessage = async () => {
        if (!input.trim() || loading) return;

        // Check for slash commands
        if (input.trim() === "/help") {
            const helpContent = KEYBOARD_COMMANDS
                .map(cmd => `**${cmd.key}**: ${cmd.description}`)
                .join("\n");

            // - **Scatter Plot**: Explore correlations (e.g., "show correlation between pain and function")
            // - **Donut Chart**: Display proportions (e.g., "show breakdown of patient categories")
            // - **Spark Chart**: Compact mini-visualizations for quick trends

            const chartHelp = `## Chart & Visualization Capabilities

Cindra can create interactive charts and visualizations from your data. Simply ask in natural language!

**Supported Chart Types:**
- **Bar Chart**: Compare values across categories (e.g., "compare outcome measures")
- **Line Chart**: Show trends over time (e.g., "plot pain levels over 12 weeks")
- **Area Chart**: Display cumulative trends (e.g., "show total engagement over time")
- **Histogram**: Visualize distributions (e.g., "show me a histogram of FABQ scores")

**Example Requests:**
- "Show me Kendall's FABQ scores over time as a line chart"
- "Create a bar chart comparing pain, function, and mood scores"
- "Plot a histogram of all patient FABQ scores"`;

            const helpMessage: Message = {
                id: Date.now().toString(),
                role: "system",
                content: `## Available Commands\n\n${helpContent}\n\n${chartHelp}`,
                timestamp: new Date(),
                isHelpMessage: true
            };

            setMessages(prev => [...prev, helpMessage]);
            setInput("");
            return;
        }

        // Check MCP connectivity before sending
        if (!mcpState.isCindraConnected) {
            const connectivityWarning: Message = {
                id: `connectivity-warning-${Date.now()}`,
                role: "system",
                content: "⚠️ **No Connection** - Cindra MCP server is not available. Please wait for connection to be restored before sending messages.",
                timestamp: new Date(),
                isConnectivityStatus: true
            };
            setMessages(prev => [...prev, connectivityWarning]);
            return;
        }

        const userMessage: Message = {
            id: Date.now().toString(),
            role: "user",
            content: input,
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInput("");
        setLoading(true);
        setHasInteracted(true);

        // Create new abort controller for this request
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        try {
            const selectedAgent = getAgentForLLM(selectedLLM.id as LLMId);
            let result: {
                text?: string;
                object?: any;
                toolResults?: any[];
                phiProtectionStatus?: {
                    anonymized?: boolean;
                    deanonymized?: boolean;
                    agentDeanonymizationEnabled?: boolean;
                    clientDeanonymizationOverride?: boolean | null;
                    encryptedMapAvailable?: boolean;
                    deanonymizationFailed?: boolean;
                    deanonymizationError?: string;
                };
            } | null;

            // Build conversation messages for the Mastra agent (excluding error messages and messages without content)
            const conversationMessages = messages
                .filter(msg => !msg.isError && msg.content)
                .map(msg => ({
                    role: msg.role as 'user' | 'assistant',
                    content: msg.content as string
                }));

            // Enhance analysis requests with formatting instructions
            let enhancedUserContent = userMessage.content;

            // Add user context if we have validated user info
            let contextualContent = enhancedUserContent;
            if (mcpAuth.validatedUser) {
                contextualContent = `[user: ${mcpAuth.validatedUser.email}${mcpAuth.validatedUser.first_name ? ` (${mcpAuth.validatedUser.first_name} ${mcpAuth.validatedUser.last_name})` : ''}]\n\n${enhancedUserContent}`;
            }

            // Add current patient context if set
            if (currentPatient) {
                contextualContent += `\n\n[CONTEXT: Current patient is ${currentPatient}. If you already have their data from previous tool calls, reuse it. Only fetch new data if needed or explicitly requested.]`;
            }

            // Add chart type preference to context
            if (selectedChartType === 'none') {
                contextualContent += `\n\n[CHART_PREFERENCE: User has explicitly selected "No Chart". Do NOT use the create_chart tool under any circumstances.]`;
            } else {
                contextualContent += `\n\n[CHART_PREFERENCE: User has selected "${selectedChartType}" as their preferred chart type. When you have data to visualize, use the create_chart tool with type="${selectedChartType}".]`;
            }

            // Add PHI deanonymization override context (only if override feature is enabled)
            // This takes precedence over the ENABLE_PHI_DEANONYMIZATION server setting
            if (isPhiPrivacyOverrideEnabled) {
                const deanonymizationAllowed = !phiPrivacyMode; // false = privacy mode ON (keep anonymized), true = privacy mode OFF (allow deanon)
                contextualContent += `\n\n[PHI_DEANONYMIZATION_ALLOWED: ${deanonymizationAllowed}]`;
            }

            // Prepare messages for the agent with auth token in runtimeContext
            result = await selectedAgent.generate({
                messages: [
                    ...conversationMessages,
                    { role: 'user', content: contextualContent }
                ] as any,
                runtimeContext: {
                    authToken: getAuthToken(),
                    userId: mcpAuth.validatedUser?.id,
                    userEmail: mcpAuth.validatedUser?.email
                }
            });

            if (result?.text) {
                let { statusMessage, mainContent } = extractStatusMessage(result.text);
                let patientName: string | undefined = currentPatient || undefined;
                let patientInfoMarkdown: string | undefined;
                let chartData: ChartData | undefined;
                let phiAnonymized: boolean | undefined;
                let phiDeanonymized: boolean | undefined;
                let phiDeanonymizationEnabled: boolean | undefined;
                let phiEncryptedMapAvailable: boolean | undefined;
                let phiViolation: boolean | undefined;

                // Extract PHI protection status from result
                if (result?.phiProtectionStatus) {
                    phiAnonymized = result.phiProtectionStatus.anonymized;
                    phiDeanonymized = result.phiProtectionStatus.deanonymized;
                    phiDeanonymizationEnabled = result.phiProtectionStatus.clientDeanonymizationOverride ?? result.phiProtectionStatus.agentDeanonymizationEnabled;
                    phiEncryptedMapAvailable = result.phiProtectionStatus.encryptedMapAvailable;
                    phiViolation = phiAnonymized && phiDeanonymized && !phiDeanonymizationEnabled;
                }

                if (result?.toolResults && Array.isArray(result.toolResults)) {
                    for (const toolResult of result.toolResults) {
                        const toolPayload = toolResult?.payload;
                        const toolName = toolPayload?.toolName;
                        const toolArgs = toolPayload?.args;
                        const toolArgResult = toolArgs?.result;

                        if (toolName === 'cindramcpserver_get_patient_information' ||
                            toolName === 'cindramcpserver_get_patient_outcomes') {
                            if (toolPayload?.result?.success) {
                                patientName = toolArgs?.full_name || toolArgs?.patient_name || toolArgs?.name || toolArgs?.fullName;
                                if (patientName) {
                                    setCurrentPatient(patientName);
                                }
                            }
                        }

                        if (toolName === 'cindramcpserver_get_patient_information') {
                            const markdown = toolPayload?.result?.markdown || toolArgResult?.markdown || toolArgResult?.structuredContent?.markdown;
                            if (markdown) {
                                // Markdown is deanonymized by agent if:
                                // 1. MCP server set phi_client_deanonymization=true (agent has permission)
                                // 2. Agent's ENABLE_PHI_DEANONYMIZATION=true (agent chooses to deanonymize)
                                patientInfoMarkdown = markdown;
                                // Get the format the agent requested to determine intent
                                const requestedFormat = toolArgs?.format || 'markdown';
                                // Strip markdown content from mainContent, keeping any introductory text or analysis
                                mainContent = stripMarkdownFromContent(mainContent, patientInfoMarkdown, requestedFormat);
                            }
                        }

                        if (toolName === 'create_chart') {
                            chartData = toolPayload?.result as ChartData;
                            patientInfoMarkdown = null; // Don't display patient markdown with a chart.
                        }
                    }
                }

                const assistantMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: "assistant",
                    content: mainContent,
                    timestamp: new Date(),
                    statusMessage,
                    patientInfoMarkdown,
                    patientName,
                    chartData,
                    phiAnonymized,
                    phiDeanonymized,
                    phiDeanonymizationEnabled: phiDeanonymizationEnabled,
                    phiEncryptedMapAvailable,
                    phiViolation
                };

                setMessages(prev => [...prev, assistantMessage]);
            }
            else {
                throw new Error("No response generated from agent");
            }

        }
        catch (agentError) {
            // Check if the error was due to abort
            if (abortController.signal.aborted) {
                const abortMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: "system",
                    content: "🛑 **Request Cancelled** - The request was cancelled by the user.",
                    timestamp: new Date(),
                    isConnectivityStatus: true
                };
                setMessages(prev => [...prev, abortMessage]);
            } else {
                console.error("[CindraChat] Agent generation error:", agentError);
                const { type, message } = categorizeError(agentError);

                const errorMessage: Message = {
                    id: (Date.now() + 1).toString(),
                    role: "assistant",
                    content: message,
                    timestamp: new Date(),
                    isError: true,
                    errorType: type
                };

                setMessages(prev => [...prev, errorMessage]);
            }
        }
        finally {
            setLoading(false);
            abortControllerRef.current = null;
            // Return focus to the textarea after response
            // Use requestAnimationFrame to ensure React has re-rendered and the textarea is enabled
            requestAnimationFrame(() => {
                textareaRef.current?.focus();
            });
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            // Reset history index when sending a new message
            setHistoryIndex(-1);
            void sendMessage();
        } else if (e.key === "Escape") {
            e.preventDefault();
            // Clear input
            setInput("");
            // Reset history index
            setHistoryIndex(-1);
            // Abort any in-progress request
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            // Get all user messages in chronological order
            const userMessages = messages.filter(msg => msg.role === "user");
            if (userMessages.length === 0) return;

            // Calculate new index (going back in history)
            const newIndex = historyIndex === -1
                ? userMessages.length - 1  // Start from most recent
                : Math.max(0, historyIndex - 1);  // Go back one, but not past the first

            setHistoryIndex(newIndex);
            setInput(userMessages[newIndex].content);
        } else if (e.key === "ArrowDown") {
            e.preventDefault();
            // Get all user messages in chronological order
            const userMessages = messages.filter(msg => msg.role === "user");
            if (userMessages.length === 0 || historyIndex === -1) return;

            // Calculate new index (going forward in history)
            const newIndex = historyIndex + 1;

            if (newIndex >= userMessages.length) {
                // Reached the end, clear input and reset index
                setInput("");
                setHistoryIndex(-1);
            } else {
                setHistoryIndex(newIndex);
                setInput(userMessages[newIndex].content);
            }
        }
    };

    const getConnectionStatus = () => {
        if (!mcpState.isCindraConnected) {
            if (mcpState.reconnectAttempts > 0) {
                return { color: "text-yellow-600", text: `Reconnecting (${mcpState.reconnectAttempts})`, icon: "reconnecting" };
            }
            return { color: "text-red-600", text: "Disconnected", icon: "disconnected" };
        }

        if (mcpAuth.tokenValidated === true) {
            return { color: "text-green-600", text: "Connected", icon: "connected" };
        } else if (mcpAuth.tokenValidated === false) {
            return { color: "text-yellow-600", text: "Connected (Auth Failed)", icon: "connected" };
        } else if (mcpAuth.hasAuthToken) {
            return { color: "text-yellow-600", text: "Connected (Validating...)", icon: "connected" };
        } else {
            return { color: "text-yellow-600", text: "Connected (No Token)", icon: "connected" };
        }
    };

    const connectionStatus = getConnectionStatus();

    return (
        <div className="flex flex-col h-full bg-white border border-gray-300 rounded-lg shadow">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <div className="flex items-center space-x-3">
                    <h3 className="text-lg font-semibold text-gray-900">Cindra Chat</h3>
                    <div className="flex items-center space-x-2">
                        {/* Connection status with icon */}
                        {connectionStatus.icon === "connected" && (
                            <WifiIcon className={`h-4 w-4 ${connectionStatus.color}`} />
                        )}
                        {connectionStatus.icon === "disconnected" && (
                            <SignalSlashIcon className="h-4 w-4 text-red-500" />
                        )}
                        {connectionStatus.icon === "reconnecting" && (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-500"></div>
                        )}
                        <span className={`text-xs font-medium ${connectionStatus.color}`}>
                            {connectionStatus.text}
                        </span>
                    </div>
                </div>

                {/* LLM Selector */}
                <Listbox value={selectedLLM} onChange={(llm) => {
                    setSelectedLLMState(llm);
                    setSelectedLLM(llm.id as LLMId); // Save to cookie
                }}>
                    <div className="relative">
                        <ListboxButton className="relative w-48 cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left border border-gray-300 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 sm:text-sm">
                            <span className="block truncate">{selectedLLM.name}</span>
                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                            </span>
                        </ListboxButton>

                        <ListboxOptions className="absolute right-0 z-10 mt-1 max-h-60 w-48 overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                            {LLM_PROVIDERS.map((provider) => (
                                <ListboxOption
                                    key={provider.id}
                                    className={({ focus }) =>
                                        `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                            focus ? "bg-blue-100 text-blue-900" : "text-gray-900"
                                        }`
                                    }
                                    value={provider}
                                >
                                    {({ selected }) => (
                                        <>
                                            <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                                                {provider.name}
                                            </span>
                                            {selected ? (
                                                <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                                    <CheckIcon className="h-5 w-5" aria-hidden="true" />
                                                </span>
                                            ) : null}
                                        </>
                                    )}
                                </ListboxOption>
                            ))}
                        </ListboxOptions>
                    </div>
                </Listbox>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map((message, index) => (
                    <div key={message.id} ref={index === messages.length - 1 ? messagesEndRef : null}>
                        {/* Status Message (if present) */}
                        {message.statusMessage && !message.isConnectivityStatus && (
                            <div className="flex justify-start mb-2">
                                <div className="max-w-3xl px-3 py-1 rounded-md bg-blue-50 text-blue-700 border border-blue-200 mr-4 text-sm italic">
                                    <div className="flex items-center">
                                        <div className="animate-pulse h-2 w-2 bg-blue-400 rounded-full mr-2"></div>
                                        <span className="select-text">{message.statusMessage}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Help Message - Special Styling */}
                        {message.isHelpMessage ? (
                            <div className="flex justify-center mb-4">
                                <div className="max-w-2xl px-6 py-4 rounded-lg bg-blue-50 border border-blue-200 text-gray-700 shadow-sm">
                                    <CindraChatMessageContent
                                        content={message.content}
                                        isUserMessage={false}
                                    />
                                </div>
                            </div>
                        ) : null}

                        {/* Connectivity Status Messages - Special Styling */}
                        {message.isConnectivityStatus && message.content ? (
                            <div className="flex justify-center mb-4">
                                <div className="px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-gray-600 text-xs flex items-center space-x-2 shadow-sm">
                                    {message.content.includes("Connection Lost") && (
                                        <SignalSlashIcon className="h-3 w-3 text-red-500" />
                                    )}
                                    {message.content.includes("Connection Restored") && (
                                        <WifiIcon className="h-3 w-3 text-green-500" />
                                    )}
                                    {message.content.includes("Reconnecting") && (
                                        <div className="animate-spin rounded-full h-3 w-3 border border-gray-400 border-t-transparent"></div>
                                    )}
                                    <span>{message.content.replace(/\*\*/g, '').replace(/🔌|✅|🔄|⚠️/g, '').trim()}</span>
                                    <span className="text-gray-400">•</span>
                                    <span className="text-gray-400">{message.timestamp.toLocaleTimeString()}</span>
                                </div>
                            </div>
                        ) : message.isHelpMessage ? null : message.content && message.content.trim() ? (
                            /* Regular Message */
                            <div className={`flex ${message.role === "user" ? "justify-end" : "justify-start"}`}>
                                <div className={`max-w-3xl rounded-lg px-4 py-2 ${
                                    message.role === "user"
                                        ? "bg-blue-600 text-white ml-4"
                                        : message.isError
                                            ? "bg-red-50 text-red-800 border border-red-200 mr-4"
                                            : "bg-gray-100 text-gray-900 mr-4"
                                }`}>
                                    {message.isError && message.errorType === "auth" && (
                                        <div className="flex items-center mb-2">
                                            <ExclamationTriangleIcon className="h-4 w-4 text-red-600 mr-2" />
                                            <span className="text-xs font-medium text-red-600">Authentication Issue</span>
                                        </div>
                                    )}
                                    {/* Render message content with code block support */}
                                    <CindraChatMessageContent
                                        content={message.content}
                                        isUserMessage={message.role === "user"}
                                        patientName={message.role === "assistant" && message.patientName ? message.patientName : undefined}
                                        phiAnonymized={message.role === "assistant" ? message.phiAnonymized : undefined}
                                        phiDeanonymized={message.role === "assistant" ? message.phiDeanonymized : undefined}
                                        phiDeanonymizationEnabled={message.role === "assistant" ? message.phiDeanonymizationEnabled : undefined}
                                        phiEncryptedMapAvailable={message.role === "assistant" ? message.phiEncryptedMapAvailable : undefined}
                                        phiViolation={message.role === "assistant" ? message.phiViolation : undefined}
                                    />
                                    <div className="mt-2 text-xs opacity-70 select-text">
                                        {message.timestamp.toLocaleTimeString()}
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        {/* Patient Info Panel - inline with chat when get_patient_information was called */}
                        {message.patientInfoMarkdown && !message.isError && (() => {
                            const sections = parsePatientInfoMarkdown(message.patientInfoMarkdown);
                            return (
                                <div className="flex justify-start mt-2 mr-4">
                                    <CindraPatientInfoPanel
                                        sections={sections}
                                        patientName={message.patientName}
                                        showHeader={true}
                                        phiAnonymized={message.phiAnonymized}
                                        phiDeanonymized={message.phiDeanonymized}
                                        phiDeanonymizationEnabled={message.phiDeanonymizationEnabled}
                                        phiEncryptedMapAvailable={message.phiEncryptedMapAvailable}
                                        phiViolation={message.phiViolation}
                                    />
                                </div>
                            );
                        })()}

                        {/* Chart Panel - inline with chat when create_chart was called */}
                        {message.chartData && !message.isError && (
                            <div className="flex justify-start mt-2 mr-4">
                                <div className="max-w-3xl w-full rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
                                    <CindraChartRenderer
                                        chartData={message.chartData}
                                        patientName={message.patientName}
                                        phiAnonymized={message.phiAnonymized}
                                        phiDeanonymized={message.phiDeanonymized}
                                        phiDeanonymizationEnabled={message.phiDeanonymizationEnabled}
                                        phiEncryptedMapAvailable={message.phiEncryptedMapAvailable}
                                        phiViolation={message.phiViolation}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ))}
                {loading && (
                    <div className="flex justify-start">
                        <div className="bg-gray-100 text-gray-900 rounded-lg px-4 py-2 mr-4">
                            <div className="flex items-center space-x-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                <span className="select-text">Cindra is thinking...</span>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Input */}
            <div className="border-t border-gray-200 p-4">
                <div className="flex space-x-2">
                    <textarea
                        ref={textareaRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyPress}
                        placeholder={hasInteracted ? "" : (mcpState.isCindraConnected ? "Ask Cindra about your healthcare data..." : "Waiting for Cindra connection...")}
                        className="flex-1 resize-none rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-500"
                        rows={1}
                        disabled={loading || !mcpState.isCindraConnected}
                    />
                    <button
                        onClick={() => void sendMessage()}
                        disabled={loading || !input.trim() || !mcpState.isCindraConnected}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Send
                    </button>
                </div>

                {/* Chart Type Selector and Patient Tag */}
                <div className="mt-3 flex items-center justify-between">
                    {/* Chart Type Selector - Left Side */}
                    <div className="flex items-center space-x-2">
                        <span className="text-xs text-gray-500 font-medium">Chart:</span>
                        <div className="flex items-center space-x-1 relative">
                            {CHART_TYPE_OPTIONS.map((option) => {
                                const Icon = option.icon;
                                const isSelected = selectedChartType === option.id;
                                return (
                                    <button
                                        key={option.id}
                                        onClick={() => {
                                            // Toggle behavior: if clicking the currently selected option, deselect it
                                            // Otherwise, select the clicked option
                                            if (isSelected) {
                                                // If clicking "No Chart" when already selected, do nothing (stay on "No Chart")
                                                // If clicking any other chart when selected, deselect it (go back to no selection)
                                                setSelectedChartType('none');
                                            } else {
                                                setSelectedChartType(option.id);
                                            }
                                        }}
                                        onMouseEnter={() => setHoveredChartType(option.id)}
                                        onMouseLeave={() => setHoveredChartType(null)}
                                        className={`p-1.5 rounded transition-colors relative ${
                                            isSelected
                                                ? option.id === 'none'
                                                    ? 'bg-red-100 text-red-600 border border-red-300'
                                                    : 'bg-blue-100 text-blue-600 border border-blue-300'
                                                : 'bg-white text-gray-400 border border-gray-200 hover:bg-gray-50 hover:text-gray-600'
                                        }`}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {hoveredChartType === option.id && (
                                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap pointer-events-none z-50">
                                                {option.name}
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Patient Tag and PHI Privacy Toggle - Right Side */}
                    <div className="flex items-center space-x-3">
                        {/* Patient Tag */}
                        {currentPatient && (
                            <div className="inline-flex items-center px-2 py-1 rounded-md bg-green-100 border border-green-300">
                                <span className="text-xs text-green-800 font-medium">{currentPatient}</span>
                                <button
                                    onClick={() => setCurrentPatient(null)}
                                    className="ml-1.5 text-green-600 hover:text-green-800 transition-colors"
                                    title="Clear current patient"
                                >
                                    <XMarkIcon className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}

                        {/* PHI Privacy Toggle (Only when override feature is enabled) */}
                        {isPhiPrivacyOverrideEnabled && (
                            <button
                                onClick={() => setPhiPrivacyMode(!phiPrivacyMode)}
                                className={`p-1.5 rounded transition-colors border ${
                                    phiPrivacyMode
                                        ? 'bg-red-100 text-red-800 border-red-300'
                                        : 'bg-yellow-100 text-yellow-800 border-yellow-300 hover:bg-yellow-200'
                                }`}
                                title={phiPrivacyMode
                                    ? 'Privacy Mode: ON - PHI data will remain anonymized'
                                    : 'Privacy Mode: OFF - PHI data will be deanonymized (default)'
                                }
                            >
                                {phiPrivacyMode ? (
                                    <LockClosedIcon className="h-4 w-4" />
                                ) : (
                                    <LockOpenIcon className="h-4 w-4" />
                                )}
                            </button>
                        )}
                    </div>
                </div>

                {/* Status indicators */}
                <div className="mt-3 flex items-center justify-end text-xs text-gray-400">
                    <div className="flex items-center space-x-1">
                        Enter /help for available commands
                    </div>
                </div>
            </div>
        </div>
    );
}
